import React, { useEffect, useMemo, useState } from "react";

import { getAccessMatrix, getMembers, saveAccessChanges } from "../services/accessService";
import type { Group, MatrixRow, Member, Notice, NoticeType, Role } from "../types/access";
import ManageRoles from "./ManageRoles";

type AccessVariant = "Default" | "Optional";

const InlineNotice: React.FC<{
  notice: Notice;
  onClose: (id: number) => void;
}> = ({ notice, onClose }) => {
  const base = "w-full rounded-md border p-3 md:p-3.5 text-sm flex items-start gap-2 shadow-sm";

  const styles: Record<NoticeType, string> = {
    success: "bg-emerald-50 border-emerald-200 text-emerald-900",
    error: "bg-rose-50 border-rose-200 text-rose-900",
    info: "bg-sky-50 border-sky-200 text-sky-900",
  };

  return (
    <div className={`${base} ${styles[notice.type]}`}>
      <div className="flex-1">{notice.message}</div>
      <button onClick={() => onClose(notice.id)}>✕</button>
    </div>
  );
};

const AccessMatrix: React.FC = () => {
  const [matrix, setMatrix] = useState<MatrixRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [updatedBy] = useState("Admin");

  const [notices, setNotices] = useState<Notice[]>([]);
  const pushNotice = (type: NoticeType, message: string) =>
    setNotices((p) => [...p, { id: Date.now(), type, message }]);

  const closeNotice = (id: number) => setNotices((p) => p.filter((n) => n.id !== id));

  useEffect(() => {
    loadMatrix();
  }, []);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const data = await getAccessMatrix();
      const membersResp = await getMembers("DEFAULT");

      setMatrix(data.matrix);
      setRoles(data.roles);
      setMembers(membersResp?.data?.members || []);
    } catch {
      pushNotice("error", "Failed to load access matrix.");
    } finally {
      setLoading(false);
    }
  };

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, MatrixRow[]>();

    matrix.forEach((r) => {
      if (!map.has(r.domain)) map.set(r.domain, []);
      map.get(r.domain)!.push(r);
    });

    return Array.from(map.entries()).map(([domain, rows]) => ({
      domain,
      rows,
    }));
  }, [matrix]);

  const toggleAccess = (rowIndex: number, roleIndex: number, variant?: AccessVariant) => {
    setMatrix((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex] };

      if (variant === "Optional" && row.optionalAccess) {
        row.optionalAccess = row.optionalAccess.map((v, i) => (i === roleIndex ? !v : v));
      } else {
        row.access = row.access.map((v, i) => (i === roleIndex ? !v : v));
      }

      next[rowIndex] = row;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      await saveAccessChanges({ matrix, roles, updatedBy });
      pushNotice("success", "Changes saved successfully.");
      await loadMatrix();
    } catch {
      pushNotice("error", "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-5 text-gray-600">Loading…</div>;

  return (
    <div className="p-4 md:p-5 font-sans">
      {notices.map((n) => (
        <InlineNotice key={n.id} notice={n} onClose={closeNotice} />
      ))}

      <div className="flex items-center mb-3 gap-3">
        <h2 className="text-xl font-semibold text-gray-800">Profile Creation</h2>
        <div className="flex-1" />
        <button
          className="bg-sky-600 text-white px-3 py-1.5 rounded-md text-sm"
          onClick={() => setShowManageRoles(true)}
        >
          Manage Roles
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="border p-3">FUNCTIONALITIES</th>
              <th className="border p-3">SUB‑FUNCTIONALITIES</th>
              {roles.map((r) => (
                <th key={r.role_id} className="border p-3">
                  {r.role_code.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {groups.map((group) => {
              const isParent = (r: MatrixRow) =>
                r.feature_code.toLowerCase() === group.domain.toLowerCase();

              const parent = group.rows.find(isParent);
              const children = group.rows.filter((r) => !isParent(r));

              const renderRows: {
                row: MatrixRow;
                variant?: AccessVariant;
              }[] = [];

              if (parent) renderRows.push({ row: parent });

              children.forEach((row) => {
                const isDashboardWidget =
                  group.domain === "dashboard" &&
                  row.feature_code !== "dashboard" &&
                  row.feature_code !== "dashboard.settings";

                if (isDashboardWidget) {
                  renderRows.push({ row, variant: "Default" });
                  renderRows.push({ row, variant: "Optional" });
                } else {
                  renderRows.push({ row });
                }
              });

              return renderRows.map(({ row, variant }, idx) => {
                if (variant === "Optional") return null;

                const rowIndex = matrix.findIndex((m) => m.feature_id === row.feature_id);

                const access = row.access;

                return (
                  <tr
                    key={`${row.feature_id}-${variant ?? "single"}`}
                    className="hover:bg-sky-50/60"
                  >
                    {idx === 0 && (
                      <td
                        rowSpan={renderRows.length}
                        className="border p-3 font-semibold lowercase"
                      >
                        {group.domain}
                      </td>
                    )}

                    <td className="border p-3 pl-8">
                      <div className="flex justify-between">
                        <span>{row.feature_code}</span>
                      </div>
                    </td>

                    {access.map((allowed, i) => (
                      <td
                        key={i}
                        className={`border p-3 text-center cursor-pointer ${
                          allowed ? "bg-emerald-100" : "bg-gray-50"
                        }`}
                        onClick={() => toggleAccess(rowIndex, i, variant)}
                      >
                        {allowed ? "✓" : ""}
                      </td>
                    ))}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3 mt-4">
        <button className="bg-gray-200 px-4 py-2 rounded-md" onClick={loadMatrix}>
          Cancel changes
        </button>
        <button
          className="bg-emerald-500 text-white px-4 py-2 rounded-md"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>

      <ManageRoles
        open={showManageRoles}
        onClose={() => setShowManageRoles(false)}
        members={members}
        roles={roles}
        createdBy={updatedBy}
      />
    </div>
  );
};

export default AccessMatrix;
